import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLocales } from "../../core/i18n";
import { useSiteSettings } from "../../core/site";
import { CONTAINER } from "./layout";
import MenuButton from "./MenuButton";
import MobileMenu from "./MobileMenu";
import NavLogo from "./NavLogo";
import { useMenuOverlay } from "./useMenuOverlay";
import { useTuckOnScroll } from "./useTuckOnScroll";

// CONTACT is the site's one accent action and the only pill in the bar.
// min-h-9 gives a 36px hit area on touch; the desktop bar is only 64px tall.
const PILL_BASE =
	"font-mono text-xs uppercase tracking-pp-ui border px-3 py-1 inline-flex items-center min-h-9 transition-colors duration-150";
const PILL_ACCENT = "border-pp-accent text-pp-accent-text hover:bg-pp-accent hover:text-pp-ink";
const PILL_ACCENT_ACTIVE = "bg-pp-accent border-pp-accent text-pp-ink";
const LANG_TOGGLE =
	"font-mono text-xs uppercase tracking-pp-label text-pp-muted hover:text-pp-ink transition-colors duration-150 inline-flex items-center justify-center min-h-9 min-w-9 px-2 py-1";
const LINK_BASE =
	"px-3 py-1 min-h-9 inline-flex items-center font-mono text-xs uppercase tracking-pp-label transition-colors duration-150";

/**
 * The sticky top bar: logo, page links, language switch and the Contact pill
 * on desktop; a hamburger opening the full-screen menu on phones, where the
 * bar also tucks away while scrolling down. The Services link only shows
 * while open to work; every other link, and the Contact pill, follows its
 * page switch in Settings.
 */
const NavBar = () => {
	const { pathname } = useLocation();
	const { t, changeLanguage, currentLang } = useLocales();
	const { openToWork, pages } = useSiteSettings();
	const [isMenuOpen, setIsMenuOpen] = useMenuOverlay(pathname);
	const tucked = useTuckOnScroll();

	// Buyer's order: what she sells, proof it worked, then the founder story.
	const navItems = [
		...(openToWork ? [{ name: t("nav.freelance"), path: "/services" }] : []),
		...(pages.impact ? [{ name: t("nav.impact"), path: "/impact" }] : []),
		...(pages.ventures ? [{ name: t("nav.startups"), path: "/ventures" }] : []),
		...(pages.journal ? [{ name: t("nav.journal"), path: "/journal" }] : []),
		...(pages.about ? [{ name: t("nav.manifesto"), path: "/about" }] : []),
	];
	const isActive = (path) => pathname === path;
	const nextLang = currentLang === "fr" ? "en" : "fr";
	const switchLanguage = () => changeLanguage(nextLang);

	return (
		<nav
			aria-label={t("ui.primaryNav")}
			className={`bg-pp-bg border-b border-pp-rule sticky top-0 z-50 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${tucked && !isMenuOpen ? "max-lg:-translate-y-full" : ""}`}
		>
			<div className={CONTAINER}>
				<div className="flex items-center justify-between h-14 sm:h-16">
					<NavLogo />
					<div className="hidden lg:flex items-center gap-1">
						{navItems.map((item) => (
							<Link
								key={item.path}
								to={item.path}
								aria-current={isActive(item.path) ? "page" : undefined}
								className={`${LINK_BASE} ${isActive(item.path) ? "bg-pp-ink text-pp-bg" : "text-pp-muted hover:text-pp-ink hover:bg-pp-ink/[0.05]"}`}
							>
								{item.name}
							</Link>
						))}
					</div>
					<div className="hidden lg:flex items-center gap-3">
						<button
							type="button"
							onClick={switchLanguage}
							lang={nextLang}
							aria-label={t("ui.switchLanguage")}
							className={LANG_TOGGLE}
						>
							{nextLang.toUpperCase()}
						</button>
						{pages.contact && (
							<Link
								to="/contact"
								className={`${PILL_BASE} ${isActive("/contact") ? PILL_ACCENT_ACTIVE : PILL_ACCENT}`}
							>
								{t("nav.contact")}
							</Link>
						)}
					</div>
					<MenuButton open={isMenuOpen} onToggle={() => setIsMenuOpen(!isMenuOpen)} />
				</div>
				{isMenuOpen && (
					<MobileMenu
						items={navItems}
						showContact={pages.contact}
						isActive={isActive}
						nextLang={nextLang}
						onSwitchLanguage={switchLanguage}
						onClose={() => setIsMenuOpen(false)}
					/>
				)}
			</div>
		</nav>
	);
};

export default NavBar;
